/**
 * Diff puro de escopos do Microsoft Graph — usado para detectar tokens defasados
 * (emitidos antes de um novo escopo entrar em MS_GRAPH_SCOPES).
 *
 * REQUISITO #1: evitar FALSO-POSITIVO. Um bug aqui marcaria TODO usuário como
 * defasado por engano, forçando reconexões desnecessárias em massa. Por isso o
 * guard anti-falso-positivo abaixo (sem dado confiável ⇒ nunca marca) e a
 * normalização cuidadosa dos dois lados antes de comparar.
 *
 * Helper puro (sem I/O, sem dependências) — testável em isolamento e consumido
 * tanto pelas functions quanto pelo script admin de reconciliação.
 */

/**
 * Escopos OIDC reservados. NÃO voltam como escopos de RECURSO do Graph no token
 * concedido (o Entra os consome no protocolo), então NUNCA devem ser exigidos na
 * comparação — se exigíssemos, marcaríamos todo mundo por engano. Constante
 * exportada e reutilizável (o script admin espelha esta mesma lista).
 */
export const RESERVED_SCOPES = ['openid', 'profile', 'email', 'offline_access'] as const

/**
 * Normaliza um scope para comparação case-insensitive por NOME FINAL:
 * - lowercase e trim;
 * - remove o prefixo de recurso do Graph mantendo só o segmento após a última
 *   '/'. Cobre 'https://graph.microsoft.com/User.Read' -> 'user.read',
 *   'https://graph.microsoft.com/.default' -> '.default' (o ponto é preservado)
 *   e 'api://<guid>/access_as_user' -> 'access_as_user'. Um nome curto sem '/'
 *   (ex.: 'User.Read') é apenas lowercased.
 *
 * Um prefixo terminado em '/' (ex.: 'https://graph.microsoft.com/') vira string
 * vazia e é descartado pelo chamador — não é um scope de recurso real.
 */
function normalizeScope(scope: string): string {
  const lowered = scope.trim().toLowerCase()
  const slash = lowered.lastIndexOf('/')
  return slash >= 0 ? lowered.slice(slash + 1) : lowered
}

/**
 * Retorna os escopos de `required` que NÃO estão presentes em `granted`
 * (ignorando os reservados OIDC). Os valores retornados são os ORIGINAIS de
 * `required` (não normalizados) — mais úteis para log do admin (ex.: 'Mail.Send'
 * em vez de 'mail.send').
 *
 * GUARD anti-falso-positivo: se `granted` for undefined/null ou array vazio,
 * retorna [] — NUNCA marcamos como defasado sem dado confiável de escopos
 * concedidos (ex.: usuário conectado antes de o backend passar a persistir
 * `microsoft.scopes`). Nesse caso o chamador deve PULAR o usuário, não marcá-lo.
 */
export function missingRequiredScopes(
  granted: string[] | undefined,
  required: readonly string[],
): string[] {
  // Sem dado confiável de escopos concedidos ⇒ não há como afirmar que falta
  // algo. Retornar [] (e não a lista inteira de required) é o que impede o
  // falso-positivo em massa.
  if (!granted || granted.length === 0) return []

  const reserved = new Set<string>(RESERVED_SCOPES)
  const grantedSet = new Set(granted.map(normalizeScope).filter((s) => s.length > 0))

  const missing: string[] = []
  for (const req of required) {
    const norm = normalizeScope(req)
    if (norm.length === 0) continue
    if (reserved.has(norm)) continue
    if (!grantedSet.has(norm)) missing.push(req)
  }
  return missing
}
